import { jsx as _jsx } from "react/jsx-runtime";
import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { List, useDynamicRowHeight, useListRef } from 'react-window';
import { Box } from '../../box';
import { useMounted } from '../../hooks';
import { styled } from '../../styled';
import { useListboxHeight } from './use-listbox-height';
const MUI_DEFAULT_ITEM_SIZE = 36;
const MUI_SMALL_ITEM_SIZE = 48;
// NOTE: replacing the listbox via `slots.listbox` drops MUI's built-in
// `.MuiAutocomplete-option` styles (padding, hover, focused, selected).
// Default-rendered options are wrapped in a `ListItemButton` which carries
// its own styles, but consumers with a custom `renderOption` rely on this
// baseline. Values match the Figma "Autocomplete dropdown" list-item spec
// (padding 8px 16px) and MUI's state backgrounds.
const ListboxRoot = styled(Box)(({ theme }) => ({
    '& .MuiAutocomplete-option:not(.MuiListItemButton-root)': {
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        cursor: 'pointer',
        padding: theme.spacing(1, 2),
        '&:hover, &.Mui-focused': {
            backgroundColor: theme.palette.action.hover
        },
        '&[aria-selected="true"]': {
            backgroundColor: theme.palette.action.selected,
            '&:hover, &.Mui-focused': {
                backgroundColor: theme.palette.action.selected
            }
        },
        '&[aria-disabled="true"]': {
            opacity: theme.palette.action.disabledOpacity,
            pointerEvents: 'none'
        }
    }
}));
const ListboxComponent = React.forwardRef((props, ref) => {
    const { children: _children, internalListRef, optionHeight, style, ...restProps } = props;
    const children = _children;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true
    });
    const itemCount = children.length;
    const itemSize = smUp ? MUI_DEFAULT_ITEM_SIZE : MUI_SMALL_ITEM_SIZE;
    const rowHeight = useDynamicRowHeight({
        defaultRowHeight: optionHeight ?? itemSize
    });
    const { className, ...divProps } = restProps;
    return (_jsx(ListboxRoot, { ref: ref, ...divProps, children: _jsx(List, { className: className, listRef: internalListRef, rowCount: itemCount, rowHeight: rowHeight, rowComponent: RowComponent, rowProps: { children }, overscanCount: 4, tagName: "ul", 
            // eslint-disable-next-line react/forbid-component-props
            style: {
                width: '100%',
                margin: 0,
                padding: 0,
                listStyle: 'none',
                ...style
            } }) }));
});
function RowComponent({ index, children, style }) {
    const component = children[index];
    return (_jsx("li", { role: "presentation", style: style, children: component }));
}
RowComponent.displayName = 'RowComponent';
export function useListBox(state, optionHeight, options = []) {
    const { paperRef, maxHeight } = useListboxHeight(state);
    const internalListRef = useListRef(null);
    const mounted = useMounted();
    const slotProps = {
        style: {
            maxHeight
        },
        optionHeight,
        internalListRef
    };
    const scrollToRow = React.useCallback((option) => {
        const { current: listApi } = internalListRef;
        if (option && listApi) {
            const index = options.indexOf(option);
            if (index !== -1) {
                listApi.scrollToRow({ index, align: 'center' });
            }
        }
    }, [internalListRef, options]);
    const onOpen = React.useCallback((option) => {
        setTimeout(() => {
            if (mounted.current) {
                if (Array.isArray(option)) {
                    scrollToRow(option[0] ?? null);
                }
                else {
                    scrollToRow(option);
                }
            }
        }, 0);
    }, [mounted, scrollToRow]);
    const onHighlightChange = React.useCallback((e, option) => {
        if (e.type !== 'mousemove') {
            scrollToRow(option);
        }
    }, [scrollToRow]);
    return {
        paperRef,
        component: ListboxComponent,
        slotProps,
        onOpen,
        onHighlightChange
    };
}
